import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  accountQuery,
  biggestCasinoWinsQuery,
  brandOriginUrl,
  buildCasinoGameFilters,
  casinoVendorsQuery,
  useAuthSession,
} from '@/api';
import {
  biggestWinPlayPath,
  casinoGameThumbUrl,
  enrichBiggestCasinoWins,
  formatBiggestWinAmount,
  formatBiggestWinTimestamp,
  isBiggestCasinoWinsEnabled,
  type EnrichedBiggestCasinoWinning,
} from '@/domain';
import { cn } from '@/lib/cn';
import { HorizontalScrollRow, Icon, Spinner } from '@/ui/common';

export interface BiggestWinsProps {
  className?: string;
}

function BiggestWinCard({ win }: { win: EnrichedBiggestCasinoWinning }) {
  const to = biggestWinPlayPath(win);
  const thumb = casinoGameThumbUrl(brandOriginUrl(), {
    ProviderName: win.ProviderName,
    VendorName: win.VendorName,
    GameTag: win.GameTag,
  });

  return (
    <Link
      to={to}
      className="relative flex h-[148px] w-[85px] shrink-0 flex-col overflow-hidden rounded-md bg-bg-elevated"
    >
      <span className="relative h-[113px] w-full shrink-0 overflow-hidden rounded-md">
        <img
          src={thumb}
          alt=""
          loading="lazy"
          className="size-full object-cover"
          onError={(event) => {
            event.currentTarget.style.visibility = 'hidden';
          }}
        />
        <span
          className="absolute left-0 top-0 flex size-6 items-center justify-center rounded-md border border-white/10 bg-bg-elevated/80 text-[14px] font-bold leading-none tracking-tight text-text-primary"
          aria-label={`Rank ${win.Ranking}`}
        >
          {win.Ranking}
        </span>
      </span>
      <span className="flex w-full flex-col items-center px-[5px] py-1.5">
        <span className="whitespace-nowrap text-[8px] leading-tight tracking-tight text-odds-default-text">
          {formatBiggestWinTimestamp(win.Timestamp)}
        </span>
        <span className="w-full truncate text-center text-[10px] font-bold leading-tight tracking-tight text-text-primary">
          {formatBiggestWinAmount(win.Amount, win.Currency)}
        </span>
      </span>
    </Link>
  );
}

/**
 * Figma BiggestWinsV2 — ranked casino win tiles (home + lobby), gated by `BiggestCasinoWins`.
 */
export function BiggestWins({ className }: BiggestWinsProps) {
  const { user } = useAuthSession();
  const { data: account } = useQuery(accountQuery());
  const enabled = isBiggestCasinoWinsEnabled(account, user);
  const accountGuid = account?.Guid ?? '';

  const vendorFilters = useMemo(
    () => buildCasinoGameFilters({ account, user, casino: true }),
    [account, user],
  );

  const winsQuery = useQuery({
    ...biggestCasinoWinsQuery(accountGuid),
    enabled: enabled && Boolean(accountGuid),
  });
  const vendorsQuery = useQuery({
    ...casinoVendorsQuery(vendorFilters),
    enabled: enabled && Boolean(vendorFilters.AccountGuid),
  });

  const wins = useMemo(
    () => enrichBiggestCasinoWins(winsQuery.data ?? [], vendorsQuery.data ?? []),
    [winsQuery.data, vendorsQuery.data],
  );

  if (!enabled) return null;

  const isLoading = winsQuery.isLoading || vendorsQuery.isLoading;
  if (!isLoading && wins.length === 0) return null;

  return (
    <section className={cn('flex flex-col gap-3', className)} aria-label="Biggest Winnings">
      <div className="flex items-center gap-2">
        <Icon name="flash" size={24} className="shrink-0 text-text-primary" />
        <h2 className="text-heading-s text-text-primary">Biggest Winnings</h2>
      </div>
      {isLoading && wins.length === 0 ? (
        <div className="flex h-[148px] items-center justify-center">
          <Spinner label="Loading biggest winnings" />
        </div>
      ) : (
        <HorizontalScrollRow
          aria-label="Biggest Winnings"
          withDesktopArrows
          className="-mx-1 gap-2.5 px-1 pb-1 pl-0"
        >
          {wins.map((win) => (
            <div
              key={`${win.VendorId}-${win.ProviderId}-${win.GameTag}-${win.Ranking}`}
              role="listitem"
            >
              <BiggestWinCard win={win} />
            </div>
          ))}
        </HorizontalScrollRow>
      )}
    </section>
  );
}
